import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SimpleLayout from '@/layouts/SimpleLayout';
import SmartLink from '@/components/SmartLink';

const SOFTWARE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'GBase Concierge',
  operatingSystem: 'Web, iOS, Android, Windows, macOS',
  applicationCategory: 'BusinessApplication',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'JPY',
    description: '無料トライアルあり',
  },
  featureList: [
    'AI受付システム',
    '多言語対応（10言語以上）',
    '来店者記憶機能',
    'リアルタイム応答',
    '店舗案内・フロアマップ',
    '予約・チケット発行',
    '顔認証 受付',
  ],
  description:
    'GBase Conciergeは、無人受付・AIコンシェルジュで商業施設・百貨店の来店者対応を自動化するAI受付システムです。',
};

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Sparticle Inc.',
  url: 'https://cs.gbase.ai',
  logo: 'https://cs.gbase.ai/title-logo.png',
  sameAs: [
    'https://x.com/sparticle_ai',
    'https://note.com/sparticle',
    'https://www.linkedin.com/company/sparticle-inc',
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'AI受付システムの導入にはどのくらいの期間がかかりますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ヒアリングから運用開始まで、最短3-4週間程度です。既存システムとの連携やFAQデータの作成状況により前後します。',
      },
    },
    {
      '@type': 'Question',
      name: '対応している言語は？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '日本語、英語、中国語、韓国語など10言語以上に対応しています。インバウンド客の多い施設での多言語対応におすすめです。',
      },
    },
    {
      '@type': 'Question',
      name: 'どのようなチャネルで利用できますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '店頭端末・サイネージ、LINE/LINE WORKS、QRコード、Webウィジェットなど、あらゆるチャネルからAI受付システムをご利用いただけます。',
      },
    },
    {
      '@type': 'Question',
      name: '導入費用はどのくらい？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '施設規模や利用チャネル数により異なります。まずは無料トライアルでお試しいただけます。詳細はお問い合わせください。',
      },
    },
    {
      '@type': 'Question',
      name: '既存の予約システムと連携できますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'はい、API連携により主要な予約システムと連携可能です。来店者からの予約依頼もスムーズに処理できます。',
      },
    },
  ],
};

type Challenge = {
  no: string;
  title: string;
  icon: string;
  badge: string;
  description: React.ReactNode;
  // 锁住 Tailwind 标准色板色值,避免 apps/website 的自定义色板缺色(如 amber)导致渲染失效
  labelStyle: React.CSSProperties;
  iconStyle: React.CSSProperties;
  boxStyle: React.CSSProperties;
  article?: { href: string; label: string };
};

const CHALLENGES: Challenge[] = [
  {
    no: '課題 1',
    title: '人件費の高騰',
    icon: 'payments',
    badge: 'コスト増大',
    labelStyle: { color: '#DC2626' }, // red-600
    iconStyle: { color: '#EF4444' }, // red-500
    boxStyle: { background: 'linear-gradient(to bottom right, #FEF2F2, #FFFFFF)' }, // red-50 → white
    description: (
      <>
        コンシェルジュ人材の確保・育成コストが増加の一途。
        <br />
        夜間・休日の対応には、さらに多くの人員が必要となり、
        <br />
        月間60万円以上の人件費がかかることも。
      </>
    ),
  },
  {
    no: '課題 2',
    title: '多言語対応の困難さ',
    icon: 'translate',
    badge: '対応できない言語が増加',
    labelStyle: { color: '#EA580C' }, // orange-600
    iconStyle: { color: '#F97316' }, // orange-500
    boxStyle: { background: 'linear-gradient(to bottom right, #FFF7ED, #FFFFFF)' }, // orange-50 → white
    description: (
      <>
        インバウンド需要が増加しているが、多言語に対応できる
        <br />
        人材の確保が困難。通訳者を配置しても、1人1言語に限られるため、
        <br />
        全言語に対応するには多くの人員が必要。
      </>
    ),
    article: {
      href: 'https://cs.gbase.ai/blog/multilingual-support-guide/',
      label: '多言語カスタマーサポートの導入ポイント',
    },
  },
  {
    no: '課題 3',
    title: '人手不足による応答遅れ',
    icon: 'groups',
    badge: 'お客様が待つことで不満が増加',
    labelStyle: { color: '#D97706' }, // amber-600
    iconStyle: { color: '#F59E0B' }, // amber-500
    boxStyle: { background: 'linear-gradient(to bottom right, #FFFBEB, #FFFFFF)' }, // amber-50 → white
    description: (
      <>
        来店者が増え、コンシェルジュだけでは応答が追いつかず。
        <br />
        お客様を待たせることで、満足度の低下やキャンセルにつながる恐れも。
      </>
    ),
    article: {
      href: 'https://cs.gbase.ai/blog/24-hour-customer-support-ai/',
      label: '24時間対応のカスタマーサポートとは',
    },
  },
];

const RelatedArticles: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-12 max-w-3xl mx-auto p-5 bg-violet-50 rounded-xl">
    <p className="text-sm text-gray-700">
      <strong className="text-violet-700">関連記事：</strong>
      {children}
    </p>
  </div>
);

const ArticleLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-600 underline hover:text-violet-700">
    {children}
  </a>
);

/**
 * Rounded(填充版) Material Symbols icon — demo 用的是这个变体。
 * 共享的 MaterialIcon 组件用 outlined 版,所以这里单独定义。
 */
const RoundedIcon: React.FC<{ name: string; className?: string; style?: React.CSSProperties }> = ({
  name,
  className = '',
  style,
}) => (
  <span
    className={className}
    style={{
      fontFamily: '"Material Symbols Rounded"',
      fontVariationSettings: '"FILL" 1, "wght" 500, "GRAD" 0, "opsz" 24',
      lineHeight: 1,
      fontSize: '1.5rem',
      ...style,
    }}
  >
    {name}
  </span>
);

const FEATURE_PALETTE = {
  blue: { iconBg: 'bg-blue-100', iconCls: 'text-blue-600', sceneBg: 'bg-blue-50', sceneLabel: 'text-blue-800' },
  green: { iconBg: 'bg-green-100', iconCls: 'text-green-600', sceneBg: 'bg-green-50', sceneLabel: 'text-green-800' },
  purple: {
    iconBg: 'bg-purple-100',
    iconCls: 'text-purple-600',
    sceneBg: 'bg-purple-50',
    sceneLabel: 'text-purple-800',
  },
} as const;

type FeatureColor = keyof typeof FEATURE_PALETTE;

const FEATURES: { icon: string; color: FeatureColor; title: string; description: string; scene: string }[] = [
  {
    icon: 'store',
    color: 'blue',
    title: '店舗検索・案内',
    description: '「〇〇店はどこ？」「3階のレストランは？」施設内のあらゆる店舗を即座に検索・案内。',
    scene: '「3階の奥、レストラン街です。エレベーターを上がって右側にお進みください」',
  },
  {
    icon: 'event',
    color: 'green',
    title: 'イベント・キャンペーン',
    description: '「今日のイベントは？」「今月のキャンペーンは？」開催中のイベント情報を即座に回答。',
    scene: '「本日は1階イベント広場で「春のフェア」を開催中。11時から抽選会もございます」',
  },
  {
    icon: 'confirmation_number',
    color: 'purple',
    title: '予約・チケット',
    description: '「〇〇店の予約したい」「チケットを購入したい」予約システムとの連携も可能。',
    scene: '「ご希望の日時は？予約可能です。チケットの発行も承ります」',
  },
  {
    icon: 'map',
    color: 'green',
    title: 'ルート案内',
    description: '「そこまでどうやっていくの？」目的地までの最適ルートを案内。',
    scene: '「現在地から〇〇店までは、エレベーターで3階へ。そのまま直進です」',
  },
  {
    icon: 'local_offer',
    color: 'purple',
    title: 'クーポン・特典',
    description: '「クーポンはない？」「会員特典は？」来店者に合わせたオファーを提案。',
    scene: '「会員様限定で10%OFFクーポンをご利用いただけます。発行しますか？」',
  },
  {
    icon: 'recommend',
    color: 'blue',
    title: 'おすすめ・レコメンド',
    description: '「何かおすすめは？」来店者の好みに応じた店舗・商品を提案。',
    scene: '「以前カフェによく行かれていましたね。同じジャンルのお店をご紹介します」',
  },
];

const USE_CASES = [
  {
    icon: 'shopping_bag',
    title: '商業施設・百貨店',
    description: '店舗案内、イベント情報、クーポン配布。買い物をサポートするAIコンシェルジュ。',
  },
  {
    icon: 'hotel',
    title: 'ホテル・旅館',
    description: '施設案内、周辺情報、予約確認。お客様をおもてなしするAIコンシェルジュ。',
  },
  {
    icon: 'business',
    title: '企業フロント',
    description: '来客案内、アポイント管理、社内呼び出し。無人受付を実現するAIコンシェルジュ。',
  },
  {
    icon: 'account_balance',
    title: '公共施設',
    description: '施設案内、申請案内、イベント案内。市民サービスを向上するAIコンシェルジュ。',
  },
];

const FAQ_ITEMS = FAQ_SCHEMA.mainEntity.map((q) => ({
  question: q.name,
  answer: q.acceptedAnswer.text,
}));

const CHANNELS = [
  {
    image: '/images/products/concierge/kiosk-smart.jpg',
    alt: '店頭端末・サイネージ',
    title: '店頭端末・サイネージ',
    subtitle: '施設内のタッチパネルから',
    note: '音声でもタッチでも対話可能。最初に触れる入口として最適。',
  },
  {
    image: '/images/products/concierge/line-works.jpg',
    alt: 'LINE Works',
    title: 'LINE / LINE WORKS',
    subtitle: 'いつでも手元のスマホから',
    note: '来店前の相談から、施設内の問い合わせまで。スタッフへの呼び出しも可能。',
  },
  {
    image: '/images/products/concierge/qr-code.jpg',
    alt: 'QR Code',
    title: 'QR Code',
    subtitle: 'カメラをかざすだけで',
    note: '館内に設置されたQRをスキャンすると、ブラウザで即座にAI対話開始。',
  },
  {
    image: '/images/products/concierge/web-widget.jpg',
    alt: 'Web Widget',
    title: 'Web Widget',
    subtitle: '施設サイトに埋め込み',
    note: '来店前に自宅で施設案内・店舗検索が可能。Webサイトにチャットウィジェットを埋め込むだけ。',
  },
];

const Concierge: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SimpleLayout>
      <Helmet>
        <title>AI受付システム「GBase Concierge」｜無人受付・AIコンシェルジュ｜GBase SUPPORT</title>
        <meta
          name="description"
          content="AI受付システム「GBase Concierge」は、無人受付・AIコンシェルジュで商業施設・百貨店の来店者対応を自動化。多言語対応、24時間365日、AI案内ロボットとして次世代の顧客体験を実現。"
        />
        <meta
          name="keywords"
          content="AI受付, AI受付システム, 無人受付, AIコンシェルジュ, AI案内ロボット, AIインフォメーション, 多言語対応, 商業施設, 百貨店, GBase Concierge"
        />
        <link rel="canonical" href="https://cs.gbase.ai/concierge" />
        <meta
          property="og:title"
          content="AI受付システム「GBase Concierge」｜無人受付・AIコンシェルジュで来店者対応を自動化"
        />
        <meta
          property="og:description"
          content="多言語対応、24時間365日、AI案内ロボットとして次世代の顧客体験を実現。商業施設・百貨店の導入実績多数。"
        />
        <meta property="og:image" content="https://cs.gbase.ai/concierge-og.png" />
        <meta property="og:url" content="https://cs.gbase.ai/concierge" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:site_name" content="GBase SUPPORT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI受付システム「GBase Concierge」" />
        <meta
          name="twitter:description"
          content="多言語対応、24時間365日、AI案内ロボットとして次世代の顧客体験を実現"
        />
        <meta name="twitter:image" content="https://cs.gbase.ai/concierge-og.png" />
        <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
        <link
          href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200"
          rel="stylesheet"
        />
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <div className="bg-white leading-relaxed text-gray-900">
        {/* ===== HERO ===== */}
        <section className="pt-32 pb-20 overflow-hidden" style={{ background: '#F3F3FE' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-100 text-violet-700 text-sm font-medium rounded-full mb-6">
                  <RoundedIcon name="support_agent" className="text-base" />
                  GBase Concierge
                </div>
                <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-6">
                  <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(135deg, #7C68FB 0%, #A78BFA 100%)' }}
                  >
                    AIコンシェルジュ
                  </span>
                  <br />
                  24時間対応・多言語対応の
                  <br />
                  次世代 AI 受付システム
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-2xl">
                  来店者の疑問に、いつでも答える。
                  <br />
                  多言語対応、24時間365日、来店者を記憶するAI受付システム。
                  <br />
                  無人受付・AIコンシェルジュで、施設案内の課題をまとめて解決します。
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <SmartLink
                    to="/contact"
                    className="px-8 py-3.5 bg-white text-violet-600 border-2 border-violet-500 hover:bg-violet-50 font-semibold rounded-full transition-all duration-300 text-lg"
                  >
                    お問い合わせ
                  </SmartLink>
                </div>
              </div>

              <div className="relative">
                <img
                  src="/images/products/concierge/concierge-hero.jpg"
                  alt="GBase Concierge 製品概要"
                  className="w-full h-auto"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== CHANNELS ===== */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-sm text-violet-500 font-semibold mb-3">あらゆる接点から</p>
              <h2 className="text-3xl font-bold">店頭端末・LINE・Web、AI受付システムをどこからでも</h2>
            </div>

            <div className="grid lg:grid-cols-4 gap-4 mb-8">
              {CHANNELS.map((ch) => (
                <div key={ch.title} className="relative group overflow-hidden rounded-2xl shadow-lg">
                  <img
                    src={ch.image}
                    alt={ch.alt}
                    loading="lazy"
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {CHANNELS.map((ch) => (
                <div key={ch.title} className="text-center">
                  <h3 className="font-bold text-lg mb-2">{ch.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{ch.subtitle}</p>
                  <p className="text-xs text-gray-500">{ch.note}</p>
                </div>
              ))}
            </div>

            <RelatedArticles>
              チャネルを超えたシームレスな顧客体験については、
              <ArticleLink href="https://cs.gbase.ai/blog/omnichannel-guide/">オムニチャネル対応のメリット</ArticleLink>
              で詳しく解説しています。
            </RelatedArticles>
          </div>
        </section>

        {/* ===== CHALLENGES ===== */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">お客様の課題</p>
              <h2 className="text-3xl font-bold">AI受付システム導入前、来店者対応の3つの課題</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {CHALLENGES.map((c) => (
                <div key={c.no} className="bg-white rounded-2xl p-8">
                  <div className="mb-6">
                    <p className="text-sm font-semibold mb-3" style={c.labelStyle}>
                      {c.no}
                    </p>
                    <h3 className="text-xl font-bold mb-2">{c.title}</h3>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <RoundedIcon name={c.icon} style={c.iconStyle} />
                      <span>{c.badge}</span>
                    </div>
                  </div>
                  <div className="rounded-xl p-4" style={c.boxStyle}>
                    <p className="text-sm text-gray-700 leading-relaxed">{c.description}</p>
                    {c.article && (
                      <p className="text-xs text-gray-600 mt-3">
                        ▶ <ArticleLink href={c.article.href}>{c.article.label}</ArticleLink>
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <RelatedArticles>
              AI受付システムの選定にお悩みの方は、
              <ArticleLink href="https://cs.gbase.ai/blog/chatbot-comparison-guide-2025/">
                チャットボット比較ガイド
              </ArticleLink>
              で主要製品の機能を比較できます。
            </RelatedArticles>
          </div>
        </section>

        {/* ===== THREE CORE CAPABILITIES ===== */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">3つの能力で、来店者対応を変革</p>
              <h2 className="text-3xl font-bold">GBase Conciergeができること</h2>
            </div>

            {/* Feature 1: Realtime */}
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-full mb-6">
                  <RoundedIcon name="speed" className="text-base" />
                  いつでも、すぐに答える
                </div>
                <h3 className="text-2xl font-bold mb-6">
                  来店者の質問に、
                  <br />
                  1秒以内で即座に回答
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8">
                  音声にもテキストにも対応。待ち時間ゼロで、来店者をスムーズに案内。
                </p>
                <ul className="space-y-4">
                  {['音声で話しかけるだけでOK', '日英中韓など10言語以上に対応', 'チャネルを問わず同じ対応'].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <RoundedIcon name="check_circle" className="text-green-500" />
                      <span className="text-gray-700">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img
                  src="/images/products/concierge/concierge-realtime.jpg"
                  alt="リアルタイム応答"
                  loading="lazy"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>

            {/* Feature 2: Memory */}
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div className="order-2 lg:order-1 relative">
                <img
                  src="/images/products/concierge/concierge-memory.jpg"
                  alt="来店者記憶"
                  loading="lazy"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-6">
                  <RoundedIcon name="history" className="text-base" />
                  来店者を覚えている
                </div>
                <h3 className="text-2xl font-bold mb-6">
                  以前の会話の続きから、
                  <br />
                  パーソナライズ対応
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8">
                  来店者を特定し、好みを学習。チャネルを超えてシームレスな体験を提供。
                </p>
                <ul className="space-y-4">
                  {[
                    '「さっき聞いたこと」を覚えている',
                    '店頭端末で聞いたことをLINEで続き',
                    '来店回数が増えるほど賢くなる',
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <RoundedIcon name="check_circle" className="text-violet-500" />
                      <span className="text-gray-700">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Feature 3: Skill */}
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 text-sm font-medium rounded-full mb-6">
                  <RoundedIcon name="extension" className="text-base" />
                  できること無限大
                </div>
                <h3 className="text-2xl font-bold mb-6">
                  店舗案内から予約管理まで、
                  <br />
                  施設に必要な業務をすべてカバー
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8">
                  複雑な業務もシンプルに。来店者のあらゆるニーズに対応。
                </p>
                <ul className="space-y-4">
                  {['店舗検索・フロア案内', 'イベント・キャンペーン情報', '予約・チケット発行'].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <RoundedIcon name="check_circle" className="text-violet-500" />
                      <span className="text-gray-700">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img
                  src="/images/products/concierge/concierge-unlimited.jpg"
                  alt="無限の可能性"
                  loading="lazy"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURES ===== */}
        <section id="features" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">機能</p>
              <h2 className="text-3xl font-bold">来店者対応をこれ1つで完結</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                店舗検索からイベント案内、予約管理まで。AI案内ロボットとして、来店者が欲しい情報を最短距離で提供します。
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => {
                const p = FEATURE_PALETTE[f.color];
                return (
                  <div
                    key={f.title}
                    className="bg-white rounded-2xl p-6 border border-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(124,104,251,0.1),0_8px_16px_rgba(0,0,0,0.04)]"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${p.iconBg}`}>
                      <RoundedIcon name={f.icon} className={`text-2xl ${p.iconCls}`} />
                    </div>
                    <h3 className="font-bold text-lg mb-3">{f.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{f.description}</p>
                    <div className={`rounded-xl p-3 ${p.sceneBg}`}>
                      <p className={`text-xs font-semibold mb-2 ${p.sceneLabel}`}>シーン例</p>
                      <p className="text-xs text-gray-600">{f.scene}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== FACE AUTH ===== */}
        <section id="face-auth" className="py-24 bg-gradient-to-br from-violet-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">顔認証 受付</p>
              <h2 className="text-3xl font-bold">顔認証 受付でスムーズな来客者管理</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                顔認証技術により、再来店時のスムーズな対応と個人認識を実現。会員登録なしで来店者を記憶するAIコンシェルジュ。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'face',
                  title: '再来店時に即座に認識',
                  desc: '以前来店したお客様を顔認証で自動判別。名前を呼んでのお出迎えが可能に。',
                },
                {
                  icon: 'how_to_reg',
                  title: '会員登録不要',
                  desc: '顔データのみで来店者を特定。面倒な登録手続きなしでシームレスな体験を提供。',
                },
                {
                  icon: 'privacy_tip',
                  title: 'プライバシー配慮',
                  desc: '顔データは暗号化保存。一定期間経過後の自動削除設定可能で、GDPR対応も完備。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-8 border border-violet-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(124,104,251,0.1),0_8px_16px_rgba(0,0,0,0.04)]"
                >
                  <div className="w-14 h-14 rounded-2xl bg-violet-500 flex items-center justify-center mb-5 shadow-lg">
                    <RoundedIcon name={item.icon} className="text-white text-3xl" />
                  </div>
                  <h3 className="font-bold text-lg mb-3">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== USE CASES ===== */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">対応業種</p>
              <h2 className="text-3xl font-bold">あらゆる業界で活用できるAI受付システム</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                商業施設・ホテル・企業フロント・公共施設で、AI案内ロボットとして導入事例が増加中。
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {USE_CASES.map((u) => (
                <div
                  key={u.title}
                  className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border border-blue-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(124,104,251,0.1),0_8px_16px_rgba(0,0,0,0.04)]"
                >
                  <div className="w-14 h-14 rounded-2xl bg-blue-500 flex items-center justify-center mb-4 shadow-lg">
                    <RoundedIcon name={u.icon} className="text-white text-3xl" />
                  </div>
                  <h3 className="font-bold text-lg mb-3">{u.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{u.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <SmartLink
                to="/solution#case-study"
                className="inline-flex items-center gap-2 px-10 py-4 border-2 border-violet-500 text-violet-600 hover:bg-violet-50 font-semibold rounded-full transition-all duration-300 text-lg"
              >
                ソリューションを見る
                <RoundedIcon name="arrow_forward" />
              </SmartLink>
            </div>

            <RelatedArticles>
              実際の導入効果については、
              <ArticleLink href="https://cs.gbase.ai/blog/chatbot-case-study/">
                チャットボット導入事例と効果
              </ArticleLink>
              で他社の成功事例をご紹介しています。
            </RelatedArticles>
          </div>
        </section>

        {/* ===== PROCESS ===== */}
        <section id="process" className="py-24 bg-gradient-to-br from-violet-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">導入までの流れ</p>
              <h2 className="text-3xl font-bold">導入までの3つのステップ</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                ヒアリングから運用開始まで、最短3-4週間でAI受付システムをご利用いただけます。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  step: 1,
                  title: 'ヒアリング（1週間）',
                  desc: '施設の課題、対応言語、連携システムをお聞きし、最適なAIコンシェルジュ構成を設計します。',
                },
                {
                  step: 2,
                  title: '設定・構築（2-3週間）',
                  desc: 'FAQデータの作成、システム連携、店頭端末の設置。AI受付システムの初期設定を一括でサポートします。',
                },
                {
                  step: 3,
                  title: '運用開始',
                  desc: '稼働後も定期的なデータ分析でAIコンシェルジュの応答精度を継続的に向上させます。',
                },
              ].map((s) => (
                <div
                  key={s.step}
                  className="bg-white rounded-2xl p-8 border border-violet-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(124,104,251,0.1),0_8px_16px_rgba(0,0,0,0.04)] relative"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-violet-700 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {s.step}
                  </div>
                  <h3 className="font-bold text-lg mb-3 mt-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section id="faq" className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm text-violet-500 font-semibold mb-3">FAQ</p>
              <h2 className="text-3xl font-bold">よくあるご質問</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                AI受付システム「GBase Concierge」に関するよくあるご質問にお答えします。
              </p>
            </div>

            <div className="space-y-4">
              {FAQ_ITEMS.map((item) => (
                <details
                  key={item.question}
                  className="group bg-gray-50 rounded-2xl border border-gray-200 p-6 hover:border-violet-300 transition-colors"
                >
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-gray-900">
                    <span>{item.question}</span>
                    <RoundedIcon
                      name="expand_more"
                      className="text-violet-500 group-open:rotate-180 transition-transform"
                    />
                  </summary>
                  <p className="mt-4 text-gray-600 leading-relaxed">{item.answer}</p>
                </details>
              ))}
            </div>

            <RelatedArticles>
              効果的なFAQ構築のポイントは、
              <ArticleLink href="https://cs.gbase.ai/blog/faq-management-efficiency/">FAQ管理の効率化</ArticleLink>や
              <ArticleLink href="https://cs.gbase.ai/blog/faq-guide-2026/">FAQ完全ガイド【2026年版】</ArticleLink>
              で詳しく解説しています。
            </RelatedArticles>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section id="contact" className="py-24 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              来店者対応を、
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #7C68FB 0%, #A78BFA 100%)' }}
              >
                今すぐ変革
              </span>
              しませんか？
            </h2>
            <p className="text-gray-400 mb-10 max-w-3xl mx-auto">
              AI受付システムで人件費を最大83%削減、24時間365日の無人受付を実現。
              <br />
              AIコンシェルジュ「GBase Concierge」の導入事例をぜひご覧ください。
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <SmartLink
                to="/contact"
                className="px-10 py-4 border-2 border-white/20 text-white font-semibold rounded-full text-lg hover:bg-white/10 transition-all"
              >
                お問い合わせ
              </SmartLink>
            </div>
          </div>
        </section>
      </div>
    </SimpleLayout>
  );
};

export default Concierge;
